/* Product reviews (Stamped) — hybrid summary layer on the shared client
   (assets/stamped-api-client.js; endpoints verified live, see that file).
   No search. All review content renders via textContent — never
   innerHTML — and nothing is fabricated outside the labeled
   design-mode path. */

if (typeof customElements.get('product-reviews-stamped') === 'undefined') {

  class ProductReviewsStamped extends HTMLElement {

    connectedCallback() {
      this.productId = this.dataset.productId;
      this.storeUrl = this.dataset.storeUrl;
      this.minHistogram = parseInt(this.dataset.minHistogram || '3', 10);
      this.showPair = this.dataset.showPair === 'true';
      this.activeFilter = null;
      this.filteredPage = 1;

      this.summaryEl = this.querySelector('[data-rev-summary]');
      this.nativeEl = this.querySelector('[data-rev-native]');
      this.emptyEl = this.querySelector('[data-rev-empty]');
      this.pairEl = this.querySelector('[data-rev-pair]');
      this.filteredEl = this.querySelector('[data-rev-filtered]');

      if (!this.productId || !this.storeUrl || !window.BSStampedClient) return;

      if (this.dataset.designMode === 'true') {
        this._renderDesignSample();
        return;
      }

      this._bindHistogramFilters();

      window.BSStampedClient.getSummary(this.storeUrl, this.productId)
        .then(summary => {
          if (!summary) return; // API down: native widget alone renders
          this._renderSummary(summary);
        });
    }

    /* --- Rendering --- */

    _renderSummary(summary) {
      this.summary = summary;

      if (summary.total === 0) {
        if (this.emptyEl) this.emptyEl.hidden = false;
        if (this.nativeEl) this.nativeEl.hidden = true;
        return;
      }

      const count = this.querySelector('[data-rev-count]');
      if (count) count.textContent = summary.total;
      const avg = this.querySelector('[data-rev-average]');
      if (avg) avg.textContent = (Math.round(summary.rating * 10) / 10).toFixed(1);
      const fill = this.querySelector('[data-rev-stars-fill]');
      if (fill) fill.style.width = Math.min(100, summary.rating / 5 * 100) + '%';
      const starsLabel = this.querySelector('[data-rev-stars-label]');
      if (starsLabel) starsLabel.setAttribute('aria-label', summary.rating + ' out of 5 stars');

      const histogram = this.querySelector('[data-rev-histogram]');
      if (histogram && summary.total >= this.minHistogram) {
        const counts = {
          '5': summary.stars[5],
          '4': summary.stars[4],
          '3': summary.stars[3],
          'low': summary.stars[2] + summary.stars[1]
        };
        const denominator = counts['5'] + counts['4'] + counts['3'] + counts['low'] || 1;
        Object.keys(counts).forEach(key => {
          const pct = Math.round(counts[key] / denominator * 100);
          const bar = this.querySelector('[data-rev-bar="' + key + '"]');
          if (bar) bar.style.width = pct + '%';
          const label = this.querySelector('[data-rev-pct="' + key + '"]');
          if (label) label.textContent = pct + '%';
        });
        histogram.hidden = false;
      }

      if (this.summaryEl) this.summaryEl.hidden = false;

      this._renderPair(summary);
    }

    _renderPair(summary) {
      if (!this.showPair || !this.pairEl) return;
      if (summary.total < 10) return;

      const pool = summary.topVoted || [];
      const positives = pool.filter(r => r.rating >= 4 && r.message);
      const criticals = pool.filter(r => r.rating <= 3 && r.message && r.votes > 0);
      const best = list => list.slice().sort((a, b) => b.votes - a.votes)[0];
      const pos = best(positives);
      const crit = criticals.length ? best(criticals) : null;
      if (!pos) return;

      this._fillQuote(this.querySelector('[data-rev-quote-positive]'), pos);
      if (crit) {
        this._fillQuote(this.querySelector('[data-rev-quote-critical]'), crit);
      } else {
        const posEl = this.querySelector('[data-rev-quote-positive]');
        if (posEl) posEl.classList.add('rev__quote--single');
      }
      this.pairEl.hidden = false;
    }

    _fillQuote(el, review) {
      if (!el) return;
      const text = el.querySelector('[data-rev-quote-text]');
      if (text) text.textContent = '“' + this._truncate(review.message, 140) + '”';
      const stars = el.querySelector('[data-rev-quote-stars]');
      if (stars) {
        stars.textContent = '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating);
        stars.setAttribute('aria-label', review.rating + ' out of 5 stars');
      }
      const author = el.querySelector('[data-rev-quote-author]');
      if (author) author.textContent = review.author;
      const verified = el.querySelector('[data-rev-quote-verified]');
      if (verified) verified.hidden = !review.verified;
      const helpful = el.querySelector('[data-rev-quote-helpful]');
      if (helpful && review.votes > 0) {
        helpful.textContent = review.votes + ' found this helpful';
        helpful.hidden = false;
      }
      el.hidden = false;
    }

    _truncate(text, max) {
      if (!text || text.length <= max) return text || '';
      const cut = text.slice(0, max);
      const space = cut.lastIndexOf(' ');
      return (space > 40 ? cut.slice(0, space) : cut) + '…';
    }

    /* --- Histogram star filter: our list replaces the native widget --- */

    _bindHistogramFilters() {
      this.querySelectorAll('[data-rev-filter]').forEach(btn => {
        btn.addEventListener('click', () => {
          const key = btn.dataset.revFilter;
          if (this.activeFilter === key) {
            this._clearFilter();
            return;
          }
          this.activeFilter = key;
          this.filteredPage = 1;
          this.querySelectorAll('[data-rev-filter]').forEach(b => {
            b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
          });
          this._loadFiltered(true);
        });
      });

      const more = this.querySelector('[data-rev-filtered-more]');
      if (more) {
        more.addEventListener('click', () => {
          this.filteredPage += 1;
          this._loadFiltered(false);
        });
      }
    }

    _clearFilter() {
      this.activeFilter = null;
      this.querySelectorAll('[data-rev-filter]').forEach(b => b.setAttribute('aria-pressed', 'false'));
      if (this.filteredEl) this.filteredEl.hidden = true;
      if (this.nativeEl) this.nativeEl.hidden = false;
    }

    _loadFiltered(reset) {
      const key = this.activeFilter;
      if (!key || !this.filteredEl) return;
      const ratings = key === 'low' ? [2, 1] : [parseInt(key, 10)];
      const page = this.filteredPage;

      Promise.all(ratings.map(n =>
        window.BSStampedClient.getReviews(this.storeUrl, this.productId, {
          take: 10, page: page, rating: n, sort: 'newest'
        })
      )).then(results => {
        if (this.activeFilter !== key) return; // stale response
        if (results.some(r => r === null)) {
          this._clearFilter();
          return;
        }

        const reviews = [];
        let total = 0;
        results.forEach(r => {
          total += r.total;
          r.reviews.forEach(x => reviews.push(x));
        });

        const list = this.querySelector('[data-rev-filtered-list]');
        if (reset && list) list.textContent = '';
        reviews.forEach(r => list.appendChild(this._buildCard(r)));

        const status = this.querySelector('[data-rev-filtered-status]');
        if (status) {
          const label = key === 'low' ? '2 stars or less' : key + ' stars';
          status.textContent = total + ' review' + (total === 1 ? '' : 's') + ' · ' + label;
        }

        const more = this.querySelector('[data-rev-filtered-more]');
        if (more) more.hidden = list.children.length >= total;

        this.filteredEl.hidden = false;
        if (this.nativeEl) this.nativeEl.hidden = true;
      });
    }

    _buildCard(r) {
      const card = document.createElement('article');
      card.className = 'rev-card';

      const head = document.createElement('div');
      head.className = 'rev-card__head';

      const name = document.createElement('span');
      name.className = 'rev-card__name';
      name.appendChild(document.createTextNode(r.author || 'Anonymous'));
      if (r.verified) {
        name.appendChild(document.createTextNode(' · '));
        const v = document.createElement('span');
        v.className = 'rev-card__verified';
        v.textContent = 'Verified';
        name.appendChild(v);
      }
      head.appendChild(name);

      const right = document.createElement('span');
      right.className = 'rev-card__right';
      const stars = document.createElement('span');
      stars.className = 'rev-card__stars';
      const rating = r.rating || 0;
      stars.textContent = '★'.repeat(rating) + '☆'.repeat(5 - rating);
      stars.setAttribute('aria-label', rating + ' out of 5 stars');
      right.appendChild(stars);
      if (r.date) {
        const date = document.createElement('span');
        date.textContent = r.date;
        right.appendChild(date);
      }
      head.appendChild(right);
      card.appendChild(head);

      if (r.title) {
        const title = document.createElement('h3');
        title.className = 'rev-card__title';
        title.textContent = r.title;
        card.appendChild(title);
      }

      const body = document.createElement('p');
      body.className = 'rev-card__body';
      body.textContent = r.message || '';
      card.appendChild(body);

      if (r.votes > 0) {
        const helpful = document.createElement('p');
        helpful.className = 'rev-card__helpful';
        helpful.textContent = r.votes + ' found this helpful';
        card.appendChild(helpful);
      }

      return card;
    }

    /* --- Theme editor preview (clearly-labeled sample, no network) --- */

    _renderDesignSample() {
      const eyebrow = this.querySelector('.rev__eyebrow');
      if (eyebrow) eyebrow.firstChild.textContent = 'Sample — from ';
      this._renderSummary({
        total: 128,
        rating: 4.8,
        stars: { 5: 96, 4: 22, 3: 6, 2: 3, 1: 1 },
        topVoted: []
      });
    }

  }

  customElements.define('product-reviews-stamped', ProductReviewsStamped);

}
